import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { UserX, Users } from 'lucide-react';
import { Card } from '@/components/common/Card';
import { EmptyState, ErrorState, LoadingState } from '@/components/common/States';
import { PageHeader } from '@/components/layout/PageHeader';
import { ROUTES } from '@/app/router/routes';
import { isApiError } from '@/utils/ApiError';
import type { ChildRef, PortalStudent, PortalSubject } from '@/types/portal';
import { ChildSwitcher } from './ChildSwitcher';
import { usePortalSubject, type PortalMode } from './usePortalSubject';

export interface PortalContext {
  subject: PortalSubject;
  /** 'me' or the child's studentId — the cache key for this subject. */
  subjectKey: string;
  student: PortalStudent | ChildRef | undefined;
  mode: PortalMode;
  withChild: (path: string) => string;
}

/**
 * The frame every portal page shares: header, child switcher, and the loading,
 * error and "no children linked" states — so each page only renders its data
 * for a subject that is already settled.
 */
export function PortalShell({
  title,
  description,
  icon,
  action,
  children,
}: {
  title: string;
  description?: string;
  icon: ReactNode;
  /** Shown beside the title when there is no child switcher to show there. */
  action?: ReactNode;
  children: (context: PortalContext) => ReactNode;
}) {
  const portal = usePortalSubject();

  if (portal.mode === 'none') {
    return <Navigate to={ROUTES.forbidden} replace />;
  }

  const who =
    portal.student === undefined
      ? undefined
      : [portal.student.studentName, portal.student.sectionName].filter(Boolean).join(' · ');

  const header = (
    <PageHeader
      title={title}
      description={[who, description].filter(Boolean).join(' — ') || undefined}
      icon={icon}
      action={
        portal.mode === 'guardian' ? (
          <ChildSwitcher
            items={portal.children}
            value={portal.student?.studentId}
            onChange={portal.selectChild}
          />
        ) : (
          action
        )
      }
    />
  );

  if (portal.isPending) {
    return (
      <div>
        {header}
        <Card>
          <LoadingState />
        </Card>
      </div>
    );
  }

  // A student role with no student record pointing at the account: the portal
  // cannot know whose data to show. Say so plainly instead of a raw 404.
  if (portal.mode === 'self' && isApiError(portal.error) && portal.error.isNotFound) {
    return (
      <div>
        {header}
        <Card>
          <EmptyState
            icon={<UserX className="h-6 w-6" aria-hidden="true" />}
            title="Your account isn't linked to a student yet"
            description="Signing in works, but the school hasn't connected this account to your student record, so there is nothing to show. Ask the school office to link it from your student profile."
          />
        </Card>
      </div>
    );
  }

  if (portal.error !== null && portal.error !== undefined) {
    return (
      <div>
        {header}
        <Card>
          <ErrorState error={portal.error} onRetry={portal.refetch} />
        </Card>
      </div>
    );
  }

  if (portal.subject === null) {
    return (
      <div>
        {header}
        <Card>
          <EmptyState
            icon={<Users className="h-6 w-6" aria-hidden="true" />}
            title="No children linked"
            description="Your account is not linked to any student yet. The school office can link your children to your account."
          />
        </Card>
      </div>
    );
  }

  return (
    <div>
      {header}
      {children({
        subject: portal.subject,
        subjectKey: portal.key,
        student: portal.student,
        mode: portal.mode,
        withChild: portal.withChild,
      })}
    </div>
  );
}
